import { Box } from "@mui/material";
import {
    GridApiCommon,
    GridInitialState,
    GridPreferencePanelsValue,
    GridToolbarContainer,
    GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { MutableRefObject, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowsMaximize, TbArrowsMinimize, TbLayoutGridRemove, TbRefresh } from "react-icons/tb";

import CommonGridSettingsButton from "@/components/CommonGridSettingsButton";
import FilterButton from "@/components/table/FilterButton";
import { LocaleStorageKeys } from "@/constants";

interface CommonTableToolbarProps {
    apiRef: MutableRefObject<GridApiCommon>;
    isFullScreen: boolean;
    onFullScreenToggle: () => void;
    onRefresh?: () => void;
    storageKey: LocaleStorageKeys;
    defaultOptions: GridInitialState;
    children?: React.ReactNode;
    newColumnNames?: string[];
    hideQuickFilter?: boolean;
    TitleComponent?: React.ReactNode;
}

const CommonTableToolbar = memo(function CommonTableToolbar({
    apiRef,
    isFullScreen,
    onFullScreenToggle,
    onRefresh,
    storageKey,
    defaultOptions,
    children,
    hideQuickFilter,
    newColumnNames,
    TitleComponent,
}: CommonTableToolbarProps) {
    const { t } = useTranslation();

    const handleGridVisibility = useCallback(() => {
        apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
    }, [apiRef]);

    const handleFullScreenToggle = useCallback(() => {
        onFullScreenToggle();
    }, [onFullScreenToggle]);

    const handleRefresh = useCallback(() => {
        if (onRefresh) {
            onRefresh();
        }
    }, [onRefresh]);

    return (
        <GridToolbarContainer className="!flex !flex-nowrap !items-center !px-2 !py-1 !gap-1">
            {!hideQuickFilter && <GridToolbarQuickFilter placeholder={t("search") + " ..."} />}
            {TitleComponent}
            <Box sx={{ flexGrow: 1 }} />

            {children}

            {onRefresh && (
                <FilterButton handleClick={handleRefresh} icon={TbRefresh} tooltip={t("tooltips.refreshTable")} />
            )}

            <FilterButton
                handleClick={handleGridVisibility}
                icon={TbLayoutGridRemove}
                tooltip={t("tooltips.ShowTableVisibility")}
            />
            <CommonGridSettingsButton
                newColumnNames={newColumnNames}
                apiRef={apiRef}
                storageKey={storageKey}
                defaultOptions={defaultOptions}
            />
            <FilterButton
                handleClick={handleFullScreenToggle}
                active={isFullScreen}
                tooltip={t("tooltips.fulscreen", { count: isFullScreen ? 2 : 1 })}
                icon={isFullScreen ? TbArrowsMinimize : TbArrowsMaximize}
            />
        </GridToolbarContainer>
    );
});

export default CommonTableToolbar;
